import { ObjectTools } from '../../../tools/object-tools';
import { SolContent, SolContentBlock, SolContentTableBlock } from '../../../models/core/sol-content';
import { SolTable } from '../../../models/overview/sol-table';
import { SolTableDef } from '../../../models/overview/sol-table-def';
import { SolTableColumnDef } from '../../../models/overview/sol-table-column-def';
import { SolType } from '../../../models/core/sol-type';
import { NavigationTarget } from '../../../tools/router-tools';

type ContentTableRow = {
  id: string;
  [key: string]: string;
};

export class SolContentComponentState {
  private constructor(
    public readonly content: SolContent,
    public readonly tablesByBlockId: Readonly<Record<string, SolTable<ContentTableRow>>>,
    public readonly linkTarget: NavigationTarget,
  ) {}

  public static of(): SolContentComponentState {
    return new SolContentComponentState(SolContent.empty(), {}, NavigationTarget.SameTab);
  }

  public withContent(content: SolContent): SolContentComponentState {
    if (ObjectTools.equals(this.content, content)) {
      return this;
    }
    return this.copy({ content }).recalculateTables();
  }

  public withLinkTarget(linkTarget: NavigationTarget): SolContentComponentState {
    if (this.linkTarget === linkTarget) {
      return this;
    }
    return this.copy({ linkTarget });
  }

  private recalculateTables(): SolContentComponentState {
    const tablesByBlockId: Record<string, SolTable<ContentTableRow>> = {};
    for (const block of this.content.blocks) {
      if (block.type !== 'table') {
        continue;
      }
      tablesByBlockId[block.id] = SolContentComponentState.toTable(block);
    }
    return this.copy({ tablesByBlockId });
  }

  private static toTable(block: SolContentTableBlock): SolTable<ContentTableRow> {
    const props: Record<string, SolType<any>> = { id: SolType.string };
    const columns = block.columns.map((columnName, index) => {
      const columnId = SolContentComponentState.toColumnId(index);
      props[columnId] = SolType.string;
      return SolTableColumnDef.of<string, never>(columnId, columnName).withNameTranslatable(false).withHideable(false);
    });

    const items = block.rows.map((row, index) => {
      const item: ContentTableRow = { id: `row-${index}` };
      block.columns.forEach((_, columnIndex) => {
        item[SolContentComponentState.toColumnId(columnIndex)] = row[columnIndex] ?? '';
      });
      return item;
    });

    return SolTable.of(
      SolTableDef.of<ContentTableRow>(
        item => item.id,
        SolType.ofObject<ContentTableRow>(`sol-content-table-${block.id}`, props),
        columns,
      ),
      items,
    );
  }

  private static toColumnId(index: number): string {
    return `column_${index}`;
  }

  private copy(props: Partial<SolContentComponentState>): SolContentComponentState {
    return ObjectTools.copy<SolContentComponentState>(this, props);
  }
}
